import { AxiosPromise } from 'axios';

import { axios } from 'src/app/utils';
import { urlsConfig } from 'src/configs';
import { queryTypes, serviceSettingTypes } from 'src/types';
import { urlHelper } from '../helpers';

export const lookupSetting = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
}: serviceSettingTypes.SettingLookupDataType): AxiosPromise<serviceSettingTypes.SettingLookupResultType> => {
  const query: queryTypes.LookupSettingQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.setting.lookupSetting), {
    blockSpinner,
  });
};
export const lookupTrainingSettingTable = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
  search,
}: serviceSettingTypes.SettingLookupDataType): AxiosPromise<serviceSettingTypes.SettingTableLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    return axios.get(
      urlHelper.addQueryParameters({ search: value }, urlsConfig.trainingObjective.lookupTrainingObjective),
      {
        blockSpinner,
      },
    );
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.trainingObjective.lookupTrainingObjective), {
    blockSpinner,
  });
};

export const createSetting = (
  form: serviceSettingTypes.SettingCreateDataType,
  formData: FormData,
  options: { blockErrorHandler: boolean },
): AxiosPromise<serviceSettingTypes.SettingCreateResultType> => {
  if (form.name) {
    formData.append('name', form.name);
  }

  if (form.creationDate) {
    formData.append('creationDate', form.creationDate.toString());
  }
  if (form.createdById) {
    formData.append('createdById', form.createdById);
  }

  if (form.agentData) {
    formData.append('agentData', form.agentData);
  }
  if (form.log) {
    formData.append('log', form.log);
  }
  if (form.comment) {
    formData.append('comment', form.comment);
  } else {
    formData.append('comment', '');
  }
  if (form.description) {
    formData.append('description', form.description);
  }

  if (form.geoDataSet) {
    formData.append('geoDataSet', form.geoDataSet);
  }

  if (form.geoSituation) {
    formData.append('geoSituation', form.geoSituation);
  }

  if (form.keyPersonSet) {
    formData.append('keyPersonSet', form.keyPersonSet);
  }

  if (form.roadToCurrentState) {
    formData.append('roadToCurrentState', form.roadToCurrentState);
  }

  return axios.post(urlsConfig.setting.createSetting, formData, {
    blockErrorHandler: true,
  });
};

export const SettingEdit = (
  form: serviceSettingTypes.SettingEditDataType,
  formData: FormData,
): AxiosPromise<serviceSettingTypes.SettingEditResultType> => {
  if (form.id !== undefined) {
    formData.append('id', form.id);
  }

  if (form.name) {
    formData.append('name', form.name);
  }

  if (form.creationDate) {
    formData.append('creationDate', form.creationDate.toString());
  }
  if (form.createdById) {
    formData.append('createdById', form.createdById);
  }

  if (form.agentData) {
    formData.append('agentData', form.agentData);
  }
  if (form.log) {
    formData.append('log', form.log);
  }

  formData.append('comment', form.comment!);

  formData.append('description', form.description!);

  if (form.geoDataSet) {
    formData.append('geoDataSet', form.geoDataSet);
  }

  if (form.geoSituation) {
    formData.append('geoSituation', form.geoSituation);
  }

  if (form.keyPersonSet) {
    formData.append('keyPersonSet', form.keyPersonSet);
  }

  if (form.roadToCurrentState) {
    formData.append('roadToCurrentState', form.roadToCurrentState);
  }
  return axios.patch(urlsConfig.setting.editSetting, formData, {
    blockErrorHandler: true,
  });
};

export const SettingDelete = ({
  id,
}: serviceSettingTypes.SettingDeleteDataType): AxiosPromise<serviceSettingTypes.SettingDeleteResultType> => {
  // Try to delete geostrategicSit data first and then delete setting data if geostrategicSit data is deleted successfully otherwise throw an error
  // Because geostrategicSit data is dependent on setting data and we don't have cascade delete on database level for this relationship
  // So we need to delete geostrategicSit data first and then setting data
  try {
    axios.delete(`${urlsConfig.geostrategicSit.delete}/${id}`);
  } catch (error) {
    throw new Error('Error while deleting geostrategicSit data');
  }

  return axios.delete(`${urlsConfig.setting.deleteSetting}/${id}`);
};

export const exportSetting = (id: string): AxiosPromise<Blob> => {
  return axios.post(`${urlsConfig.setting.exportSetting}/${id}`, null, {
    responseType: 'blob',
  });
};

export const importSetting = (eventName: string, id: string, file: File): AxiosPromise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  return axios.post(`${urlsConfig.setting.importSetting}/${eventName}/${id}`, formData);
};
